import React, { useEffect, useState } from 'react';
import { DemoRequestForm } from './DemoRequestForm';

/**
 * Phase-2 Plans + trial-status page (UXeng). Route: /plans.
 *
 * Provider-neutral UI (PM): reads the LIVE meter (`/api/account/inference-status`, extended by
 * eng-1 to also return plan/planStatus/trialEndsAt) — NOT a billing provider. The Upgrade CTA
 * hands off to eng-1's Stripe TEST-mode checkout via POST /api/billing/checkout {planId} → {url};
 * the UI only knows "get a checkout url + redirect", so it stays provider-agnostic.
 *
 * Tier $ are RATIFIED (2026-07-11): Free $0 / Pro $49 / Business $199 / Enterprise (sales).
 * Per-tier feature/quota copy is GTM-owned — the strings below are placeholders to swap for GTM's.
 */

type PlanId = 'FREE' | 'PRO' | 'BUSINESS' | 'ENTERPRISE';

interface InferenceStatus {
  state: 'ok' | 'outage' | 'cap';
  usage: { used: number; limit: number; windowResetsAt: string };
  plan?: PlanId | 'TRIAL'; // eng-1 bonus fields (from the one endpoint)
  planStatus?: string;
  trialEndsAt?: string;
}

interface Tier {
  id: PlanId;
  name: string;
  price: string;
  cadence: string;
  annual?: string; // e.g. "$39/mo billed annually"
  popular?: boolean;
  blurb: string;
  plusOf?: string; // "Everything in {plusOf}, plus:"
  features: string[];
  cta: 'current' | 'checkout' | 'sales' | 'downgrade';
}

// Copy = GTM funnel doc §11 (ratified). "Conversation" counts carry the §-footnote below
// (advertised convos vs the meter's enforced completions — GTM/PM/eng-1 reconciling the
// headroom + the exact ~N).
const TIERS: Tier[] = [
  {
    id: 'FREE', name: 'Free', price: '$0', cadence: '', blurb: 'Launch an AI agent on your site, free forever.',
    features: [
      '1 AI agent', '1 team seat', '500 conversations / mo', 'Website chat widget',
      'Auto-build your agent from your website', 'Basic analytics', '30-day history',
      'Community support', '"Powered by Biombo" badge',
    ], cta: 'downgrade',
  },
  {
    id: 'PRO', name: 'Pro', price: '$49', cadence: '/mo', annual: '$39/mo billed annually', popular: true,
    blurb: 'Everything you need to run customer conversations at scale.', plusOf: 'Free',
    features: [
      '3 AI agents', '3 team seats', '2,000 conversations / month (need more? upgrade to Business anytime)',
      'All channels & integrations (WhatsApp, Slack, +more)', 'Remove Biombo branding',
      'Advanced analytics & reporting', '1-year history', 'Email support',
    ], cta: 'checkout',
  },
  {
    id: 'BUSINESS', name: 'Business', price: '$199', cadence: '/mo', annual: '$159/mo billed annually',
    blurb: 'Scale across your team with control and support.', plusOf: 'Pro',
    features: [
      'Unlimited AI agents', '10 team seats', '10,000 conversations / month (need more? contact us for custom volume)',
      'API access', 'SSO & roles/permissions', '2-year history', 'Priority support',
    ], cta: 'checkout',
  },
  {
    id: 'ENTERPRISE', name: 'Enterprise', price: 'Custom', cadence: '',
    blurb: 'Custom scale, security, and support.', plusOf: 'Business',
    features: [
      'Custom conversation volume', 'White-label branding', 'SCIM', 'Bring-your-own AI key (optional)',
      'Dedicated success manager & SLA', 'Security review & custom contract',
    ], cta: 'sales',
  },
];

const daysUntil = (iso?: string): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 86_400_000) : 0;
};

interface PlansPageProps {
  /**
   * F2 — where checkout returns the user. Omitted (the /plans route) → the server defaults to
   * /plans, so standalone behaviour is unchanged. The in-wizard PlansModal passes "/onboarding"
   * so upgrading from inside onboarding RESUMES the wizard instead of abandoning it. The value is
   * allowlisted server-side (billing.safeReturnPath) — that allowlist stays closed; do not widen it.
   */
  returnPath?: string;
}

export const PlansPage: React.FC<PlansPageProps> = ({ returnPath }) => {
  const [status, setStatus] = useState<InferenceStatus | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState<PlanId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    fetch('/api/account/inference-status')
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const currentPlan = status?.plan;

  // Manage-subscription → eng-1's Stripe Customer Portal (POST /api/billing/portal → {url}).
  // Shown for self-serve paid plans (PRO/BUSINESS have a Stripe customer from checkout); on
  // 400/409 (no customer yet) or a not-yet-shipped endpoint we surface a graceful note rather
  // than a dead button. Same shape whether it ships in this seam or the portal fast-follow.
  const openPortal = async () => {
    setPortalBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/portal', { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'No active subscription to manage yet.');
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
      else throw new Error('Billing portal unavailable right now.');
    } catch (e: any) {
      setError(e.message);
      setPortalBusy(false);
    }
  };

  const startCheckout = async (planId: PlanId) => {
    setCheckoutBusy(planId);
    setError(null);
    try {
      // Provider-neutral hand-off: eng-1's server creates the (Stripe test-mode) Checkout
      // Session and returns its url; we just redirect. No provider specifics in the UI.
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // returnPath is sent only when a host surface supplied one (see PlansPageProps); the
        // server allowlists it and falls back to /plans.
        body: JSON.stringify(returnPath ? { planId, returnPath } : { planId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not start checkout. Please try again.');
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
      else throw new Error('Checkout unavailable right now.');
    } catch (e: any) {
      setError(e.message);
      setCheckoutBusy(null);
    }
  };

  // Return from eng-1's Stripe checkout: success_url=/plans?billing=success (tier already
  // written by the webhook → reflected via inference-status) / cancel_url=…?billing=cancelled.
  const billingReturn = new URLSearchParams(window.location.search).get('billing');

  const ratio = status && status.usage.limit > 0 ? status.usage.used / status.usage.limit : 0;
  const resetsIn = daysUntil(status?.usage.windowResetsAt);
  const trialDays = daysUntil(status?.trialEndsAt);

  return (
    <div className="min-h-screen bg-canvas text-text-strong px-4 py-10">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold mb-1">Plans & billing</h1>
        <p className="text-gray-400 mb-8">Upgrade any time. No card needed for the trial.</p>

        {billingReturn === 'success' && (
          <div className="bg-green-900/30 border border-green-700/60 text-green-100 rounded-2xl p-4 mb-6">
            🎉 You're upgraded — your new plan is active.
          </div>
        )}
        {billingReturn === 'cancelled' && (
          <div className="bg-canvas border border-white/10 text-gray-300 rounded-2xl p-4 mb-6">
            Checkout cancelled — no charge was made. You can upgrade any time.
          </div>
        )}

        {/* Trial / usage status — reads the live meter */}
        {status && (
          <div className="bg-surface border border-white/5 rounded-3xl p-6 mb-10">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="font-semibold">
                Current plan: <span className="text-primary">{status.plan || 'Trial'}</span>
                {status.planStatus && <span className="text-gray-500 text-sm"> · {status.planStatus}</span>}
              </div>
              {trialDays !== null && (
                <div className="text-sm text-gray-300">Free trial · {trialDays} {trialDays === 1 ? 'day' : 'days'} left</div>
              )}
              {(status.plan === 'PRO' || status.plan === 'BUSINESS') && (
                <button
                  onClick={openPortal}
                  disabled={portalBusy}
                  className="text-sm text-primary hover:underline disabled:opacity-50"
                >
                  {portalBusy ? 'Opening…' : 'Manage subscription'}
                </button>
              )}
            </div>
            <div className="w-full bg-canvas h-2 rounded-full overflow-hidden">
              <div className="bg-primary h-full transition-all" style={{ width: `${Math.min(100, ratio * 100)}%` }} />
            </div>
            <div className="text-sm text-gray-400 mt-2">
              {status.usage.used.toLocaleString()} / {status.usage.limit.toLocaleString()} conversations this period
              {/* Truth split (PM): trial CLOCK = trialEndsAt (shown above); the recurring quota
                  reset (windowResetsAt) is only surfaced on a PAID plan, to avoid a double
                  countdown during the trial. Both come from the real backend fields — no
                  parallel model. */}
              {trialDays === null && resetsIn !== null && (
                <> · resets in {resetsIn} {resetsIn === 1 ? 'day' : 'days'}</>
              )}
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

        {/* Tier cards — mobile: stacked; wider: 4-up */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => {
            const isCurrent = currentPlan === t.id;
            return (
              <div
                key={t.id}
                className={`bg-surface rounded-3xl p-6 border flex flex-col relative ${
                  t.popular ? 'border-primary/60' : 'border-white/5'
                }`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-6 bg-primary text-white text-[10px] font-black px-2 py-0.5 rounded-full tracking-wide">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-lg font-bold">{t.name}</div>
                <div className="mt-2 mb-1">
                  <span className="text-3xl font-black">{t.price}</span>
                  <span className="text-gray-500 text-sm">{t.cadence}</span>
                </div>
                {t.annual && <div className="text-xs text-gray-500 mb-1">{t.annual}</div>}
                <p className="text-gray-400 text-sm mb-4">{t.blurb}</p>
                {t.plusOf && <div className="text-xs font-semibold text-gray-400 mb-2">Everything in {t.plusOf}, plus:</div>}
                <ul className="space-y-2 text-sm text-gray-300 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-primary">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {isCurrent ? (
                    <button disabled className="w-full py-3 rounded-2xl bg-canvas text-gray-400 font-bold cursor-default">
                      Current plan
                    </button>
                  ) : t.cta === 'checkout' ? (
                    <button
                      onClick={() => startCheckout(t.id)}
                      disabled={checkoutBusy === t.id}
                      className="w-full py-3 rounded-2xl bg-primary text-white font-bold hover:bg-primary-dark transition-all disabled:opacity-50"
                    >
                      {checkoutBusy === t.id ? 'Starting…' : `Upgrade to ${t.name}`}
                    </button>
                  ) : t.cta === 'sales' ? (
                    // Enterprise → the SAME book-a-demo pipe (PM): POST /api/demo-request, one
                    // lead-capture funnel reused (Enterprise-contact = a demo request).
                    <button
                      onClick={() => setDemoOpen(true)}
                      className="w-full py-3 rounded-2xl border border-white/15 font-bold hover:border-white/30 transition-all"
                    >
                      Contact sales
                    </button>
                  ) : (
                    <button disabled className="w-full py-3 rounded-2xl bg-canvas/50 text-gray-500 font-bold cursor-default">
                      Included
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-600 text-center mt-8">
          Billed monthly · cancel any time · prices in USD. Conversation limits reset each period, not a trial expiry.
          {/* LOCKED (PM ruling / GTM §11, 2026-07-12): N=6. Prod couldn't drive p50/p90
              (Conversation rows=0, UsageRecord unlinked) → conservative ×6 fallback; the footnote
              number EQUALS the enforcement unit, so "≈ 6 responses" = exactly what we budget.
              Enforced completion caps = advertised × 6 (eng-1/gates concern, not copy). */}
          <br />A conversation ≈ 6 AI responses; heavy usage may reach the limit sooner and routes to an upgrade, never a hard stop.
        </p>
      </div>

      <DemoRequestForm
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        heading="Talk to sales"
        blurb="Enterprise plans are tailored — tell us about your team and we'll be in touch."
      />
    </div>
  );
};

export default PlansPage;
